import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';

/**
 * ViewPermissionModal component
 * Modal for viewing detailed information about a permission
 */
const ViewPermissionModal = ({ open, permission, onClose, getPermissionUsage }) => {
  const [usageData, setUsageData] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [usageError, setUsageError] = useState(null);

  // Fetch usage data when modal opens
  useEffect(() => {
    if (open && permission) {
      fetchUsageData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, permission]);

  const fetchUsageData = async () => {
    if (!permission) return;

    try {
      setLoadingUsage(true);
      setUsageError(null);
      const data = await getPermissionUsage(permission.id);
      setUsageData(data);
    } catch (err) {
      setUsageError(err.message || 'Failed to fetch usage data');
    } finally {
      setLoadingUsage(false);
    }
  };

  if (!permission) {
    return null;
  }

  const hasParent = permission.parent_path || permission.parent_permission;
  const hasChildren = permission.children && permission.children.length > 0;
  const isUsed = usageData && usageData.usage_count > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Permission Details
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
          {permission.category === 'system' ? 'System Permission' : 'Project Permission'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Basic Information */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Name */}
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Name
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {permission.name}
                </Typography>
              </Box>

              {/* Path */}
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Path
                </Typography>
                <Chip
                  label={permission.path}
                  size="medium"
                  sx={{ fontFamily: 'monospace', fontWeight: 'medium' }}
                />
              </Box>

              {/* Description */}
              {permission.description && (
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Description
                  </Typography>
                  <Typography variant="body2">
                    {permission.description}
                  </Typography>
                </Box>
              )}

              {/* Badges */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {permission.is_core && (
                  <Chip
                    icon={<LockIcon />}
                    label="Core Permission"
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {permission.is_parent && (
                  <Chip
                    icon={<FolderIcon />}
                    label={`${permission.children_count || 0} Children`}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          </Paper>

          {/* Hierarchy Information */}
          {(hasParent || hasChildren) && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Hierarchy
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Parent */}
              {hasParent && permission.parent_permission && (
                <Box sx={{ mb: hasChildren ? 2 : 0 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Parent Permission
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <FolderIcon color="primary" fontSize="small" />
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {permission.parent_permission.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {permission.parent_permission.path}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Children */}
              {hasChildren && (
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Child Permissions ({permission.children.length})
                  </Typography>
                  <List dense sx={{ mt: 1 }}>
                    {permission.children.map((child) => (
                      <ListItem
                        key={child.id}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1
                        }}
                      >
                        <DescriptionIcon fontSize="small" sx={{ mr: 2, color: 'text.secondary' }} />
                        <ListItemText
                          primary={child.name}
                          secondary={child.path}
                          secondaryTypographyProps={{
                            sx: { fontFamily: 'monospace', fontSize: '0.75rem' }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Paper>
          )}

          {/* Usage Information */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Usage Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {loadingUsage ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={30} />
              </Box>
            ) : usageError ? (
              <Alert severity="error">{usageError}</Alert>
            ) : isUsed ? (
              <Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Chip
                    icon={<GroupIcon />}
                    label={`${usageData.usage_count} Roles`}
                    color="success"
                  />
                  <Chip
                    icon={<PersonIcon />}
                    label={`${usageData.total_users_affected} Users Affected`}
                    color="info"
                  />
                </Box>

                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Roles using this permission:
                </Typography>
                <List dense sx={{ mt: 1 }}>
                  {usageData.roles.map((role) => (
                    <ListItem
                      key={role.role_id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1
                      }}
                    >
                      <ListItemText
                        primary={role.role_name}
                        secondary={`${role.role_type.toUpperCase()} • ${role.user_count} users`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : (
              <Alert severity="info">
                This permission is not currently assigned to any roles.
              </Alert>
            )}
          </Paper>

          {/* Metadata */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Metadata
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Permission ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {permission.id}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Display Order
                </Typography>
                <Typography variant="body2">
                  {permission.display_order}
                </Typography>
              </Box>

              {permission.created_at && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body2">
                    {new Date(permission.created_at).toLocaleString()}
                  </Typography>
                </Box>
              )}

              {permission.updated_at && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Updated At
                  </Typography>
                  <Typography variant="body2">
                    {new Date(permission.updated_at).toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewPermissionModal;
